import { Component, Input, Output, OnInit, OnChanges, EventEmitter, ChangeDetectionStrategy } from '@angular/core';

import { Direction, DocumentTypeEntity, DocumentConfig, DocumentConfigSearchDisplay } from '../models';
import { Subject } from 'rxjs';
import { DocumentConfigSearch, DocumentConfigService } from '../services';

import { FormsModule } from '@angular/forms';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'ep-document-type-selector',
    templateUrl: './document-type-selector.component.html',
    styleUrls: ['./document-type-selector.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule]
})
export class DocumentTypeSelectorComponent implements OnInit, OnChanges {
    @Input() direction = Direction.In;
    @Input() titleText = '';
    @Input() showAllOption = true;
    @Input() showAllOptionText = 'All';
    @Input() selectFirstItemIfOnlyOne = false;
    @Input() distributionObject: DocumentTypeEntity;
    @Input() documentType: DocumentConfig = null;
    @Output() documentTypeChange = new EventEmitter<DocumentConfig>();

    public documentTypes: DocumentConfigSearchDisplay[];
    public selectedDocumentType: DocumentConfigSearchDisplay = null;
    public searchObject: DocumentConfigSearch;

    initializedSubject = new Subject<void>();
    public initialized = this.initializedSubject.asObservable();

    constructor(private documentConfigService: DocumentConfigService) { }

    async ngOnInit() {
        this.initializeSearchObject();
        await this.search();
        this.initializedSubject.next();
        this.initializedSubject.complete();
    }

    initializeSearchObject() {
        this.searchObject = this.searchObject || new DocumentConfigSearch(this.direction);
    }

    public ngOnChanges(changes: any) {
        if (changes.distributionObject && this.distributionObject) {
            this.refresh(this.distributionObject.Id);
        }
        if (changes.documentType && this.documentType) {
            this.setSelected(this.documentType.DocumentConfigsId, false);
        }
    }

    public async refresh(distributionObjectId: number = null) {
        this.initializeSearchObject();
        if (this.searchObject) { // Only available when init has been called
            this.searchObject.distributionObjectId = distributionObjectId;
            await this.search();
        }
    }

    public async reset() {
        this.documentType = null;
        await this.refresh();
    }

    async search() {
        const documentTypeSearchDisplays = await this.documentConfigService.getDocumentConfigs(this.searchObject);

        documentTypeSearchDisplays.forEach(dtsd => {
            dtsd['DisplayName'] = `${dtsd.FormatName} / ${dtsd.VariantName} / ${dtsd.VersionName}`;
            if (!this.searchObject.distributionObjectId) { // If no specific distribution-object is chosen - show the name of the distribution-object
                dtsd['DisplayName'] = `${dtsd['DisplayName']} [${dtsd.DistributionObjectName}]`
            }
        });

        documentTypeSearchDisplays.sort((e1: any, e2: any) => e1.DisplayName.localeCompare(e2.DisplayName));

        this.documentTypes = documentTypeSearchDisplays;
        if (this.documentType && this.documentType.DocumentConfigsId) {
            this.setSelected(this.documentType.DocumentConfigsId, false);
        }
        else if (this.selectFirstItemIfOnlyOne && this.documentTypes.length === 1) {
            this.setSelected(this.documentTypes[0].DocumentConfigId, true);
        }
    }

    public async setSelected(documentConfigId: number, emitChange: boolean = false) {
        if (this.documentTypes) {
            this.selectedDocumentType = this.documentTypes.find(e => e.DocumentConfigId === documentConfigId) || null;
            this.documentType = null;
            if (this.selectedDocumentType) {
                this.documentType = await this.documentConfigService.getDocumentConfig(this.selectedDocumentType.DocumentConfigId);
            }
            this.documentTypeChange.emit(this.documentType);
        }
    }

    documentTypeChanged(documentType: DocumentConfigSearchDisplay) {
        if (documentType) {
            this.setSelected(documentType.DocumentConfigId, true);
        }
        else {
            this.setSelected(-1, true);
        }
    }
}
